/**
 * BatchImageUploader - Upload zone with workspace gallery selection
 *
 * Unified preview showing both uploaded files and workspace images
 */

import { Upload, Images, X, FileImage, Cloud } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/v2/ui';
import type { WorkspaceImage } from '@/lib/api/batch';
import { cn } from '@/lib/utils';

interface BatchImageUploaderProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  selectedImages: WorkspaceImage[];
  onSelectedImagesChange: (images: WorkspaceImage[]) => void;
  onOpenGallery: () => void;
  maxImages?: number;
  disabled?: boolean;
}

export function BatchImageUploader({
  files,
  onFilesChange,
  selectedImages,
  onSelectedImagesChange,
  onOpenGallery,
  maxImages = 10,
  disabled = false,
}: BatchImageUploaderProps) {
  const { t } = useTranslation();
  const totalImages = files.length + selectedImages.length;
  const hasImages = totalImages > 0;

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const removeSelectedImage = (imageId: string) => {
    onSelectedImagesChange(selectedImages.filter((img) => img.id !== imageId));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    const remainingSlots = maxImages - totalImages;
    const filesToAdd = newFiles.slice(0, remainingSlots);
    if (filesToAdd.length > 0) {
      onFilesChange([...files, ...filesToAdd]);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled || totalImages >= maxImages) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    const imageFiles = droppedFiles.filter((file) => file.type.startsWith('image/'));
    const remainingSlots = maxImages - totalImages;
    const filesToAdd = imageFiles.slice(0, remainingSlots);

    if (filesToAdd.length > 0) {
      onFilesChange([...files, ...filesToAdd]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {t('batch.uploadImages')}
        </h3>
        {hasImages && (
          <span className="text-sm text-neutral-500">
            {totalImages} / {maxImages} {t('common.images')}
          </span>
        )}
      </div>

      {/* Upload + Gallery Buttons */}
      <div className="flex gap-3">
        {/* Upload Button */}
        <label
          className={cn(
            'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed transition-colors cursor-pointer',
            disabled || totalImages >= maxImages
              ? 'border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 cursor-not-allowed opacity-50'
              : 'border-neutral-300 dark:border-neutral-600 hover:border-orange-400 dark:hover:border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-950/20'
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            disabled={disabled || totalImages >= maxImages}
            className="hidden"
          />
          <Upload className="h-5 w-5 text-neutral-500" />
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {t('common.upload')}
          </span>
        </label>

        {/* Gallery Button */}
        <Button
          variant="outline"
          onClick={onOpenGallery}
          disabled={disabled || totalImages >= maxImages}
          iconLeft={<Images className="h-5 w-5" />}
          className="flex-1"
        >
          Aus Galerie
        </Button>
      </div>

      {/* Unified Preview - Shows both uploaded files AND workspace images */}
      {hasImages && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              {t('batch.selectedImages', { count: totalImages })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onFilesChange([]);
                onSelectedImagesChange([]);
              }}
              disabled={disabled}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
            >
              <X className="h-4 w-4 mr-1" />
              Alle entfernen
            </Button>
          </div>

          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
            {/* Uploaded Files */}
            {files.map((file, index) => {
              const previewUrl = URL.createObjectURL(file);
              return (
                <div
                  key={`file-${index}`}
                  className="relative group aspect-square rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800"
                >
                  <img
                    src={previewUrl}
                    alt={file.name}
                    className="w-full h-full object-cover"
                    onLoad={() => URL.revokeObjectURL(previewUrl)}
                  />

                  {/* Badge: Hochgeladen */}
                  <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-primary-500 text-white text-xs rounded flex items-center gap-1">
                    <FileImage className="h-3 w-3" />
                  </div>

                  {/* Remove Button */}
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    disabled={disabled}
                    className={cn(
                      'absolute top-0.5 right-0.5 p-1 rounded-full',
                      'bg-black/60 text-white hover:bg-black/80',
                      'opacity-0 group-hover:opacity-100 transition-opacity'
                    )}
                    aria-label={t('common.remove')}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}

            {/* Workspace Images */}
            {selectedImages.map((img) => {
              // Use /thumb/ proxy endpoint for authenticated access (like ImageCard does)
              const thumbUrl = `/thumb/${img.id}`;

              return (
                <div
                  key={`workspace-${img.id}`}
                  className="relative group aspect-square rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800"
                >
                  <img
                    src={thumbUrl}
                    alt="Workspace Bild"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />

                  {/* Badge: Aus Galerie */}
                  <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-orange-500 text-white text-xs rounded flex items-center gap-1">
                    <Cloud className="h-3 w-3" />
                  </div>

                  {/* Remove Button */}
                  <button
                    type="button"
                    onClick={() => removeSelectedImage(img.id)}
                    disabled={disabled}
                    className={cn(
                      'absolute top-0.5 right-0.5 p-1 rounded-full',
                      'bg-black/60 text-white hover:bg-black/80',
                      'opacity-0 group-hover:opacity-100 transition-opacity'
                    )}
                    aria-label={t('common.remove')}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
